"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface SliderNavButtonsProps {
  onPrev: () => void;
  onNext: () => void;
  canScrollPrev?: boolean;
  canScrollNext?: boolean;
  /** Use dark theme (for dark backgrounds) */
  darkMode?: boolean;
  className?: string;
}

/**
 * SliderNavButtons - Consistent navigation buttons for all carousel/slider sections
 * 
 * Usage:
 * <SliderNavButtons 
 *   onPrev={() => scrollPrev()} 
 *   onNext={() => scrollNext()} 
 *   canScrollPrev={canScrollPrev}
 *   canScrollNext={canScrollNext}
 * />
 */
export function SliderNavButtons({
  onPrev,
  onNext,
  canScrollPrev = true,
  canScrollNext = true,
  darkMode = false,
  className = "",
}: SliderNavButtonsProps) {
  return (
    <div className={cn("flex items-center gap-2 flex-shrink-0", className)}>
      {/* Previous Button */}
      <button
        onClick={onPrev}
        disabled={!canScrollPrev}
        className={cn(
          "w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-200",
          darkMode
            ? cn(
                "border",
                canScrollPrev
                  ? "bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30"
                  : "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
              )
            : cn(
                "border shadow-sm",
                canScrollPrev
                  ? "bg-white border-gray-200 text-gray-700 hover:border-teal-600 hover:text-teal-600"
                  : "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
              )
        )}
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {/* Next Button */}
      <button
        onClick={onNext}
        disabled={!canScrollNext}
        className={cn(
          "w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-200",
          canScrollNext
            ? "bg-teal-600 text-white hover:bg-teal-500 shadow-lg shadow-teal-600/20"
            : darkMode
              ? "bg-white/10 text-white/30 cursor-not-allowed"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
        )}
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
